import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import BodyText from 'components/Luxkit/Typography/BodyText'
import ImageHero from 'components/Common/ImageHero'
import Image from 'components/Common/Image'
import Spacing from 'components/Common/Spacing'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidLinkedinIcon from 'components/Luxkit/Icons/solid/SolidLinkedinIcon'
import Heading from 'components/Luxkit/Typography/Heading'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import { mediaQueryUp } from 'components/utils/breakpoint'

import WhyWorkForUs from './WhyWorkForUs/WhyWorkForUs'
import OpenPositions from './OpenPositions'
import MissionAndValues from './MissionAndValues/WhyWorkForUs'
import LeadershipTeam from './LeadershipTeam/LeadershipTeam'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import Clickable from 'components/Common/Clickable'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import { scrollToHash } from 'lib/window/scrollUtils'
import { useLocation } from 'react-router-dom'

const OFFICE_GALLERY_IMAGES: Array<App.Image> = [
  { id: 'careers-breakout' },
  { id: 'careers-kitchen' },
  { id: 'careers-lunch' },
  { id: 'careers-maldives' },
  { id: 'careers-reception' },
  { id: 'careers-atrium' },
  { id: 'careers-south' },
]

const VIDEO_IDS: Array<string> = [
  '493536108',
  '378171905',
  '309987458',
  '250216191',
]

export const enum CAREERS_PAGE_ANCHORS {
  OPEN_POSITIONS = 'open-positions'
}

const BestPlaces = styled(LayoutContainer)`
  display: flex;
  flex-direction: column;
  text-align: center;

  img {
    max-width: 100%;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    text-align: left;
    align-items: center;

    p {
      margin-top: 0;
      margin-left: ${rem(64)};
    }

    img {
      display: block;
      width: 374px;
      max-width: initial;
    }
  }
`

const LinkedInWrapper = styled.div`
  margin-top: ${rem(48)};
  margin-bottom: ${rem(96)};
  text-align: center;
  > a {
    width: 100%;
    max-width: 280px;
  }
`
const SpacedSubtitle = styled(Subtitle)`
  margin-top: ${rem(32)};
`

const VideoContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(20)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
  }

  > * {
    aspect-ratio: 16 / 9;
    max-width: 100%;
  }
`

const VimeoWrapper = styled(Clickable)`
  > * {
    pointer-events: none;
  }
`

function CareersPage() {
  const showLightbox = useContext(LightBoxContext)
  const location = useLocation()

  useEffect(() => {
    scrollToHash(location.hash, true)
  }, [location])

  return <>
    <HeadData
      title="Careers at Luxury Escapes"
      description="Are you passionate about travel or technology and looking for an exciting new opportunity? Come and join Luxury Escapes, recognised as one of the 'Best Places to Work 2021 & 2023' by the Australian Financial Review."
    />
    <ImageHero
      alt="Luxury Escapes Melbourne Office"
      id="careers-melbourne-office"
      brightness={-35}
    >
      <LayoutContainer>
        <Heading
          variant="heading2"
          align="center"
          colour="neutral-eight"
          as="h1"
        >
          Careers at Luxury Escapes
        </Heading>
        <SpacedSubtitle
          variant="subtitle1"
          align="center"
          colour="neutral-eight"
          forwardedAs="p"
        >
          Are you passionate about travel or technology and looking for an exciting new opportunity?
          <br />
          Come and join Luxury Escapes, recognised as one of the 'Best Places to Work 2021 & 2023' by the Australian Financial Review.
        </SpacedSubtitle>
      </LayoutContainer>
    </ImageHero>
    <Pane>
      <PaneBody size="lg xl-y">
        <BestPlaces>
          <Image id="best-places-to-work" alt="Best Places to work" enhancedFormats={false} />
          <BodyText variant="large" as="p">
            Our team is a collective of curious, forward-thinking people, driven to challenge the industry and ourselves to bring unforgettable travel experiences to our customer community.
          </BodyText>
        </BestPlaces>
      </PaneBody>
    </Pane>
    <LayoutContainer>
      <ImageGallery images={OFFICE_GALLERY_IMAGES} />
    </LayoutContainer>
    <WhyWorkForUs />
    <Pane>
      <PaneBody size="xl-y md">
        <Heading
          variant="heading3"
          align="center"
          as="h2"
        >
          Watch our team videos
        </Heading>
      </PaneBody>
    </Pane>
    <Pane>
      <LayoutContainer>
        <VideoContainer>
          {VIDEO_IDS.map(id => <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={id} />)} key={id}>
            <VimeoVideo vimeoVideoId={id} />
          </VimeoWrapper>)}
        </VideoContainer>
      </LayoutContainer>
    </Pane>
    <Pane>
      <PaneBody size="lg xl-y">
        <LayoutContainer>
          <Heading
            id={CAREERS_PAGE_ANCHORS.OPEN_POSITIONS}
            variant="heading3"
            align="center"
            as="h2"
          >
            Current opportunities
          </Heading>
          <Spacing mt={32}>
            <Heading variant="heading5" align="center" as="h3">Open Positions</Heading>
          </Spacing>
          {!IS_SSR && <OpenPositions />}
        </LayoutContainer>
      </PaneBody>
    </Pane>
    <MissionAndValues />
    <LeadershipTeam />
    <Pane>
      <PaneBody size="lg xl-y">
        <LayoutContainer>
          <Heading
            variant="heading3"
            align="center"
            as="h2"
          >
            Connect with us on LinkedIn
          </Heading>
          <LinkedInWrapper>
            <TextButton
              kind="primary"
              href="https://www.linkedin.com/company/luxury-escapes/"
              target="_blank"
              endIcon={<SolidLinkedinIcon/>}
              size="large"
            >Connect</TextButton>
          </LinkedInWrapper>
        </LayoutContainer>
      </PaneBody>
    </Pane>
  </>
}

export default CareersPage
